/**
 * Coach IA WebSocket Provider
 * Manages real-time WebSocket connections for coaching messages
 * 
 * Phase 2: Real-Time Delivery
 * Date: 2026-07-06
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

// Create Context
const CoachWebSocketContext = createContext(null);

/**
 * Provider component that manages WebSocket connection for Coach IA
 * Wraps the entire app and provides real-time message capability
 */
export const CoachWebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [messageQueue, setMessageQueue] = useState([]);
  const [stats, setStats] = useState({
    totalConnections: 0,
    messagesReceived: 0,
    messagesSent: 0
  });

  // Get user from Redux
  const user = useSelector(state => state.user);
  const userId = user?._id || user?.id;
  const token = user?.token;

  // Get environment config
  const wsEnabled = process.env.REACT_APP_COACH_WEBSOCKET_ENABLED === 'true';
  const wsUrl = process.env.REACT_APP_COACH_WEBSOCKET_URL || 
                `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/coach/ws`;

  /**
   * Connect to WebSocket server
   */
  const connectWebSocket = useCallback(() => {
    if (!wsEnabled || !userId || !token || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const url = new URL(wsUrl);
      url.searchParams.append('userId', userId);
      url.searchParams.append('token', token);

      const ws = new WebSocket(url.toString());

      ws.onopen = () => {
        console.log('[Coach WS] Connected');
        setWsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Send any queued messages
        messageQueue.forEach(msg => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(msg));
          }
        });
        setMessageQueue([]);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Update stats
          setStats(prev => ({
            ...prev,
            messagesReceived: prev.messagesReceived + 1
          }));

          // Dispatch custom event for components to listen
          window.dispatchEvent(new CustomEvent('coach:message', { detail: message }));
        } catch (error) {
          console.warn('[Coach WS] Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.warn('[Coach WS] Error:', error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('[Coach WS] Disconnected');
        setWsConnected(false);

        // Attempt reconnection with exponential backoff
        if (wsEnabled && userId && token && reconnectAttemptsRef.current < 10) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current += 1;
          
          setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[Coach WS] Connection error:', error);
      setWsConnected(false);
    }
  }, [wsEnabled, userId, token, wsUrl, messageQueue]);

  /**
   * Send message through WebSocket
   */
  const sendMessage = useCallback((data) => {
    if (!wsRef.current) {
      console.warn('[Coach WS] WebSocket not connected');
      return false;
    }

    try {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(data));
        setStats(prev => ({
          ...prev,
          messagesSent: prev.messagesSent + 1
        }));
        return true;
      } else if (wsRef.current.readyState === WebSocket.CONNECTING) {
        // Queue message for later
        setMessageQueue(prev => [...prev, data]);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('[Coach WS] Send error:', error);
      return false;
    }
  }, []);

  /**
   * Close WebSocket connection
   */
  const closeWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setWsConnected(false);
    }
  }, []);

  // Connect on mount, reconnect when user changes
  useEffect(() => {
    if (wsEnabled && userId && token) {
      connectWebSocket();
    }

    return () => {
      // Don't close on unmount - keep connection alive
      // Only close if provider unmounts completely
    };
  }, [wsEnabled, userId, token, connectWebSocket]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const value = {
    wsConnected,
    sendMessage,
    closeWebSocket,
    stats,
    wsEnabled
  };

  return (
    <CoachWebSocketContext.Provider value={value}>
      {children}
      {/* Development indicator */}
      {process.env.NODE_ENV === 'development' && wsEnabled && (
        <div
          style={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            fontSize: '11px',
            backgroundColor: wsConnected ? '#4caf50' : '#ff9800',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '3px',
            zIndex: 9999,
            fontFamily: 'monospace'
          }}
          title={wsConnected ? 'WebSocket connected' : 'Using polling fallback'}
        >
          {wsConnected ? '● WS' : '◯ Poll'}
        </div>
      )}
    </CoachWebSocketContext.Provider>
  );
};

/**
 * Hook to use Coach WebSocket in components
 */
export const useCoachWebSocket = () => {
  const context = useContext(CoachWebSocketContext);
  
  if (!context) {
    throw new Error('useCoachWebSocket must be used within CoachWebSocketProvider');
  }

  return context;
};

export default CoachWebSocketProvider;
